import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import FunctionalBannerImage from "../../../../assets/technologies/tools-and-languages/uvm/uvmbannerimage.png";
import ProfileImage from "../../../../assets/blogpages/person.png";
import "./FunctionalVerification.css";
import { AnimatePresence, motion } from "framer-motion";

// Simple highlight component: wraps matches in <mark>
const Highlight = ({ text = "", query = "" }) => {
  if (!query) return <>{text}</>;
  const parts = text.split(new RegExp(`(${query})`, "gi"));
  return parts.map((part, i) =>
    part.toLowerCase() === query.toLowerCase()
      ? <mark key={i}>{part}</mark>
      : part
  );
};

const FunctionalVerification = () => {
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [showThankYou, setShowThankYou] = useState(false);
  const [replyVisible, setReplyVisible] = useState({});

  const closeOverlay = () => setShowThankYou(false);
  const toggleReply = id =>
    setReplyVisible(prev => ({ ...prev, [id]: !prev[id] }));

  // refs for scrolling
  const sectionRefs = {
    intro: useRef(null),
    methodologies: useRef(null),
    testbench: useRef(null),
    constrainedRandom: useRef(null),
    coverage: useRef(null),
    assertions: useRef(null),
    uvm: useRef(null),
    toolFlows: useRef(null),
    challenges: useRef(null),
    trends: useRef(null),
    comments: useRef(null),
  };

  const scrollTo = ref =>
    ref.current && ref.current.scrollIntoView({ behavior: "smooth", block: "center" });

  // scroll to first match on search
  useEffect(() => {
    if (!searchTerm) return;
    const firstMark = document.querySelector("mark");
    firstMark?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [searchTerm]);

  // fade‐in/out on scroll
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) e.target.classList.add("visible");
        else e.target.classList.remove("visible");
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll(".fade-section").forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  // auto‐dismiss popup
  useEffect(() => {
    if (showThankYou) {
      const t = setTimeout(() => setShowThankYou(false), 2000);
      return () => clearTimeout(t);
    }
  }, [showThankYou]);

  const handleSearch = () => setSearchTerm(searchInput.trim());
  const handleCommentSubmit = e => {
    e.preventDefault();
    setShowThankYou(true);
    e.target.reset();
  };

  return (
    <div className="blog-post-page functional-verification">
      {/* Banner */}
      <div className="blog-banner fade-section">
        <div className="banner-overlay">
          <h1>Functional Verification</h1>
          <div className="breadcrumb">
            <Link to="/services/asic-verification">Services</Link> | Functional Verification
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      <div className="mobile-nav fade-section">
        <div className="search-box">
          <input
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
            type="text"
            placeholder="Search keyword..."
            className="search-input"
          />
          <button onClick={handleSearch} className="search-button">Search</button>
        </div>
        <div className="table-of-contents">
          <h4>In This Article</h4>
          <ul>
            {Object.entries(sectionRefs).map(([key, ref]) => (
              <li key={key}>
                <button onClick={() => scrollTo(ref)}>
                  {{
                    intro: "Introduction",
                    methodologies: "Verification Methodologies",
                    testbench: "Testbench Architecture",
                    constrainedRandom: "Constrained-Random",
                    coverage: "Functional Coverage",
                    assertions: "Assertions & Checks",
                    uvm: "UVM Environment",
                    toolFlows: "Tool Flows & Automation",
                    challenges: "Challenges & Best Practices",
                    trends: "Future Trends",
                    comments: "Comments",
                  }[key]}
                </button>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Content */}
      <div className="blog-content-container">
        <div className="blog-left-column">
          <img
            className="post-main-image fade-section"
            src={FunctionalBannerImage}
            alt="Functional Verification"
          />
          <div className="post-info-row fade-section">
            <span className="post-author">By John Verifier</span>
            <span className="post-category">Category: ASIC Verification</span>
            <button className="comment-link" onClick={() => scrollTo(sectionRefs.comments)}>
              3 Comments
            </button>
          </div>

          {/* 1. Introduction */}
          <h2 ref={sectionRefs.intro} className="fade-section">Introduction to Functional Verification</h2>
          <p className="fade-section">
            <Highlight
              text="Functional verification is the process of confirming that an ASIC’s RTL implementation behaves as intended before silicon is manufactured."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Unlike formal methods, functional approaches rely on simulation, random stimulus, and coverage metrics to exercise design behavior under realistic scenarios."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="A robust functional verification strategy combines directed tests, constrained-random patterns, assertion-based checking, and comprehensive coverage closure."
              query={searchTerm}
            />
          </p>

          {/* 2. Verification Methodologies */}
          <h2 ref={sectionRefs.methodologies} className="fade-section">Verification Methodologies</h2>
          <p className="fade-section">
            <Highlight
              text="Directed testing targets specific blocks or corner cases with hand-crafted stimulus for quick sanity checks and bug hunting."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Constrained-random techniques leverage randomized inputs within defined constraints to uncover unexpected interactions across modules."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage-driven verification dynamically adjusts stimulus generation based on coverage gaps, aiming to maximize functional exposure with minimal test count."
              query={searchTerm}
            />
          </p>

          {/* 3. Testbench Architecture */}
          <h2 ref={sectionRefs.testbench} className="fade-section">Testbench Architecture</h2>
          <p className="fade-section">
            <Highlight
              text="A modular testbench separates drivers, monitors, scoreboards, and checkers, enabling reuse and parallel development."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Scoreboards compare DUT outputs against golden models to validate correctness, while coverage monitors collect coverage data."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Transactors abstract low-level signal toggles into high-level transactions, simplifying stimulus generation and analysis."
              query={searchTerm}
            />
          </p>

          {/* 4. Constrained-Random */}
          <h2 ref={sectionRefs.constrainedRandom} className="fade-section">Constrained-Random Verification</h2>
          <p className="fade-section">
            <Highlight
              text="User-defined constraints ensure generated stimuli respect protocol rules and legal state transitions."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Coverage feedback loops steer the random generator toward unexercised scenarios, closing coverage holes efficiently."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Review constraints regularly to avoid over-constraining, which can mask corner-case bugs."
              query={searchTerm}
            />
          </p>

          {/* 5. Functional Coverage */}
          <h2 ref={sectionRefs.coverage} className="fade-section">Functional Coverage</h2>
          <p className="fade-section">
            <Highlight
              text="Coverage domains include statement, branch, toggle, FSM state, and assertion coverage, each providing different insights."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Cross-coverage tracks interactions between different functional elements, essential for verifying complex corner cases."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Regularly analyze coverage reports to prioritize new tests and refine existing scenarios."
              query={searchTerm}
            />
          </p>

          {/* 6. Assertions & Checks */}
          <h2 ref={sectionRefs.assertions} className="fade-section">Assertion-Based Verification</h2>
          <p className="fade-section">
            <Highlight
              text="Embedded assertions catch protocol violations in real time, reducing debug effort by pinpointing failure points."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use temporal assertions for multi-cycle properties and immediate assertions for combinational checks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Integrate assertion coverage into your metrics to ensure checks themselves are exercised."
              query={searchTerm}
            />
          </p>

          {/* 7. UVM Environment */}
          <h2 ref={sectionRefs.uvm} className="fade-section">UVM Environment</h2>
          <p className="fade-section">
            <Highlight
              text="Universal Verification Methodology (UVM) provides a standardized framework for building scalable, reusable testbenches in SystemVerilog."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Components such as uvm_driver, uvm_monitor, uvm_agent, and uvm_sequence simplify integration of new IP blocks."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Leverage UVM factory overrides and configuration database to customize tests without code changes."
              query={searchTerm}
            />
          </p>

          {/* 8. Tool Flows & Automation */}
          <h2 ref={sectionRefs.toolFlows} className="fade-section">Tool Flows & Automation</h2>
          <p className="fade-section">
            <Highlight
              text="Integrate simulation runs into CI/CD pipelines to catch regressions early on every commit."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Automate waveform generation, coverage aggregation, and report publishing to dashboards."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Use containerized environments to ensure consistency across engineers and labs."
              query={searchTerm}
            />
          </p>

          {/* 9. Challenges & Best Practices */}
          <h2 ref={sectionRefs.challenges} className="fade-section">Challenges & Best Practices</h2>
          <p className="fade-section">
            <Highlight
              text="Testbench complexity can explode for large designs—modularization and hierarchy are key to manageability."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Flaky tests undermine confidence—identify non-deterministic stimulus and isolate or fix root causes promptly."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Maintain a living test plan: retire stale tests, add targeted sequences, and track coverage trends over time."
              query={searchTerm}
            />
          </p>

          {/* 10. Future Trends */}
          <h2 ref={sectionRefs.trends} className="fade-section">Future Trends in Functional Verification</h2>
          <p className="fade-section">
            <Highlight
              text="AI-driven stimulus generation will propose new test scenarios based on code changes and past failures."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Virtual platforms and emulation-in-the-loop will enable early software-driven verification of hardware designs."
              query={searchTerm}
            />
          </p>
          <p className="fade-section">
            <Highlight
              text="Visualization tools will provide interactive exploration of coverage and waveforms, accelerating root-cause analysis."
              query={searchTerm}
            />
          </p>

          {/* Comment Section */}
          <div className="comment-section" ref={sectionRefs.comments}>
            <h3>3 Comments</h3>

            {/* Comment 1 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">Sim Guru</span>
                    <span className="comment-date">May 5, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Great overview of constrained-random techniques—helped me fine-tune my testbench!
              </p>
            </div>

            {/* Comment 2 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">CoverMaster</span>
                    <span className="comment-date">May 6, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                Including assertion coverage was an eye-opener—I hadn’t tracked that before.
              </p>
              <button className="reply-button" onClick={() => toggleReply("covermaster")}>Reply</button>
              <AnimatePresence initial={false}>
                {replyVisible["covermaster"] && (
                  <motion.div
                    className="leave-comment"
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <h3>Reply to CoverMaster</h3>
                    <form onSubmit={handleCommentSubmit}>
                      <div className="form-group">
                        <label>Name</label>
                        <input type="text" placeholder="Your name..." required />
                      </div>
                      <div className="form-group">
                        <label>Message</label>
                        <textarea placeholder="Your reply..." required />
                      </div>
                      <button type="submit" className="send-message-btn">Reply</button>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Comment 3 */}
            <div className="comment-card fade-section">
              <div className="comment-header">
                <img src={ProfileImage} alt="User" className="comment-profile-img" />
                <div>
                  <div className="comment-author-date">
                    <span className="comment-author">UVM Enthusiast</span>
                    <span className="comment-date">May 7, 2025</span>
                  </div>
                </div>
              </div>
              <p className="comment-text">
                UVM tips were spot-on—factory overrides really saved me hours of debugging!
              </p>
            </div>

            {/* Leave a comment */}
            <div className="leave-comment fade-section">
              <h3>Leave a comment</h3>
              <p>Have questions or feedback? Fill out the form below!</p>
              <form onSubmit={handleCommentSubmit}>
                <div className="form-group">
                  <label>Name</label>
                  <input type="text" placeholder="Name here..." required />
                </div>
                <div className="form-group">
                  <label>Message</label>
                  <textarea placeholder="Message here…" required />
                </div>
                <button type="submit" className="send-message-btn">Send Message</button>
              </form>
            </div>
          </div>
        </div>

        {/* Sidebar */}
        <div className="blog-right-column fade-section">
          <div className="search-box">
            <input
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
              type="text"
              placeholder="Search keyword..."
              className="search-input"
            />
            <button onClick={handleSearch} className="search-button">Search</button>
          </div>
          <div className="table-of-contents">
            <h4>In This Article</h4>
            <ul>
              {Object.entries(sectionRefs).map(([key, ref]) => (
                <li key={key}>
                  <button onClick={() => scrollTo(ref)}>
                    {{
                      intro: "Introduction",
                      methodologies: "Verification Methodologies",
                      testbench: "Testbench Architecture",
                      constrainedRandom: "Constrained-Random",
                      coverage: "Functional Coverage",
                      assertions: "Assertions & Checks",
                      uvm: "UVM Environment",
                      toolFlows: "Tool Flows & Automation",
                      challenges: "Challenges & Best Practices",
                      trends: "Future Trends",
                      comments: "Comments",
                    }[key]}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Thank You Popup */}
        <AnimatePresence>
          {showThankYou && (
            <motion.div
              className="thank-you-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeOverlay}
            >
              <motion.div
                className="thank-you-content"
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                transition={{ type: "spring", stiffness: 300 }}
                onClick={e => e.stopPropagation()}
              >
                <div className="checkmark-container">
                  <motion.svg
                    className="checkmark"
                    viewBox="0 0 52 52"
                    initial={{ pathLength: 0 }} animate={{ pathLength: 1 }}
                    transition={{ duration: 1, ease: "easeInOut" }}
                  >
                    <circle cx="26" cy="26" r="25" stroke="#007bff" strokeWidth="2" />
                    <path d="M16 27.5L23 34.5L36 19.5"
                      stroke="#007bff" strokeWidth="3"
                      strokeLinecap="round" strokeLinejoin="round"
                    />
                  </motion.svg>
                </div>
                <h3>Thank you for the feedback!</h3>
                <p>We appreciate your comment.</p>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default FunctionalVerification;